import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPageInfo, selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchAclWithProfileEntries, fetchAclWithProfileEntriesForExport } from 'common/services/api/Acls/AclEntries.thunk';
import { FetchLicenseDetailsResponse } from 'common/services/api/Licenses/LicenseThunkTypeDefs';
import { ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';
import { GetDeleteDialogContent } from 'features/aclEntries/AclEntriesDeleteDialogContent';
import { GetDrawerContent } from 'features/aclEntries/AclEntriesDrawerContent';
import { AclEntriesDrawerProperties, AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';

import { addNewFilter, clearFilters, removeFilter, selectFilters } from './licenseAclEntriesSlice';

export function LicenseAclEntriesTab() {
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const filters = useAppSelector(selectFilters);
    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const [processing, setProcessing] = useState<boolean>(false);
    const [defaultSortModel] = usePalmSortModel('principalName');
    const dispatch = useAppDispatch();

    const { tenantId = '', licenseId = '' } = useParams();

    const fetchGridData = usePalmFetchPaginatedEntities(
        'ACL Entries',
        { tenantId, licenseId },
        fetchAclWithProfileEntries,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchAclWithProfileEntriesForExport({
            tenantId,
            licenseId,
            search: search ?? '',
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            rowsCount: paginationGridData?.rowsCount,
            signal: signal
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(
            paginationModel.page,
            filters.keywords[0],
            paginationGridData?.sortModel ?? defaultSortModel
        );
    };

    const handleSortModelChange = (model: GridSortModel) => {
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(
            paginationGridData?.currentPage ?? 0,
            filters.keywords[0],
            model
        );
    };

    const refreshDataGrid = () => {
        fetchGridData(
            paginationGridData?.currentPage ?? 0,
            filters.keywords[0],
            paginationGridData?.sortModel ?? defaultSortModel
        );
    };

    const actionDefinitions =  [
        { action: Operations.Edit, disabled: shouldDisableButtons },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'principalName', columnName: 'User / Group', columnType: ColumnType.First, copy: true },
        { columnId: 'resourceName', columnName: 'ACL Resource', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'conditionsToDisplay', columnName: 'ACL Condition', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'accessName', columnName: 'ACL Access', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'startDate', columnName: 'ACL Start Date', columnType: ColumnType.DatetimeTooltip },
        { columnId: 'endDate', columnName: 'ACL End Date', columnType: ColumnType.DatetimeTooltip },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action, size: 0.5 }
    ];

    const pageInfo = useAppSelector(selectPageInfo) as FetchLicenseDetailsResponse;

    function GetLicenseAclDrawer() {
        const drawerProps : AclEntriesDrawerProperties = {
            tenantId: pageInfo?.tenantId ?? '',
            tenantName: pageInfo?.tenantName ?? '',
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense ?? false,
            principalId: '',
            principalName: '',
            aclPage: AclEntriesPage.License,
            licenseId: pageInfo?.id,
            licenseBillingSystemName: pageInfo?.billingSystemName,
            licenseBillingRecord: pageInfo?.billingRecord
        };
        return GetDrawerContent(drawerProps);
    }

    function GetAclDeleteDialogContent() {
        const dialogProps = {
            aclPage: AclEntriesPage.License,
            licenseBillingRecord: pageInfo?.billingRecord
        };
        return GetDeleteDialogContent(dialogProps);
    }

    return <PalmDataGrid
        title='ACL Entry'
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={GetLicenseAclDrawer()}
        deleteFunction={GetAclDeleteDialogContent()}
        filter={filterProp}
        searchFilterDisabled={filters.keywords.length !== 0}
        refreshDataGridClick={refreshDataGrid}
        isCreateButtonDisabled={shouldDisableButtons}
        sortModel={paginationGridData?.sortModel ?? defaultSortModel}
        paginationMode={paginationMode}
        paginationInfo={{
            handlePageChange,
            handleSortModelChange,
            take: DISPLAYED_GRID_ROWS_COUNT
        }}
        shouldShowExportButton={true}
        fetchExportData={fetchExportData}
    />;
}
